import clsx from 'clsx';
import React from 'react';

import ExpandOrgChartPlusIcon from '@components/Images/Rebranding/ExpandOrgChartPlusIcon';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, TrackingLocation, click } from '@tracking';

import OrgChartCompanyReport from './CompanyReport';
import useOrgChart from './OrgChartContext';
import { OrgChartEmployeeTopLevelProps } from './interfaces';

const OrgChartEmployeeTopLevel = ({
  name,
  reports,
  id,
  position,
  imageUrl,
  isRootLevel,
}: OrgChartEmployeeTopLevelProps) => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.OrgChartEmployeeTopLevel,
  });
  const { selectedEmployee, selectEmployee, isEmployeeExpanded, toggleEmployeeExpansion } =
    useOrgChart();

  const hasReports = reports != null && reports.length > 0;
  const isSelected = selectedEmployee === id;
  const isExpanded = id ? isEmployeeExpanded(id) : false;

  const handleClick = () => {
    // root levels should not be selectable if they have no reports
    if (isRootLevel && !hasReports) {
      return;
    }
    if (id) {
      trackEvent(click({ clickTarget: ClickTarget.OrgChartEmployeeTopLevel, value: id }));
      selectEmployee(id);
      toggleEmployeeExpansion(id);
    }
  };

  return (
    <div className="relative">
      <button
        id={id ? `employee-${id}` : undefined}
        className={
          'relative m-0 h-[170px] w-[250px] appearance-none border-none bg-white p-0 pb-6 pt-10 text-left hover:cursor-pointer'
        }
        onClick={handleClick}
      >
        <div
          className={clsx(
            'absolute bottom-0 flex h-[130px] w-full flex-col justify-center p-16 transition duration-300',
            {
              'outline outline-1 outline-grayscale-gray': !isExpanded && !isSelected,
              '!outline outline-primary-magenta': isExpanded || isSelected,
            }
          )}
        >
          <p className="m-0 line-clamp-1 pt-16 text-caption-lg leading-tight">{name}</p>
          <span className="m-0 line-clamp-2 text-caption-sm leading-tight text-grayscale-gray">
            {position}
          </span>
        </div>
        <div className="absolute -top-4 left-24 flex h-90 w-90 items-center justify-center rounded-full bg-white p-2">
          <div className="h-[78px] w-[78px] rounded-full bg-gray-300">
            <img
              loading="lazy"
              className="h-[78px] w-auto rounded-full object-scale-down"
              src={imageUrl}
              alt={`${name} portrait`}
            />
          </div>
        </div>
        {hasReports && (
          <div className="absolute -bottom-16 left-1/2 z-10 flex h-[30px] min-w-[50px] max-w-[100px] -translate-x-1/2 flex-row items-center justify-center gap-4 bg-white p-6">
            <p className="relative text-caption-sm" title="number of direct reports">
              {reports?.length ?? 0}
            </p>
            {!isRootLevel && (
              <ExpandOrgChartPlusIcon
                className={clsx('h-16 w-16 transform transition duration-300', {
                  'rotate-45 text-grayscale-gray': isExpanded,
                  'text-primary-magenta': !isExpanded,
                })}
              />
            )}
          </div>
        )}
        <div
          className={clsx(
            'absolute bottom-0 left-0 z-[1] h-[130px] transform bg-primary-magenta transition-all duration-300 ease-in-out',
            {
              'w-8': isSelected,
              'w-0': !isSelected,
            }
          )}
        ></div>
      </button>

      {isExpanded && !isRootLevel && hasReports && (
        <div className="relative mt-0">
          {/* Vertical line on the left side */}
          <div className="absolute bottom-36 left-[25px] top-0 z-[10] w-2 -translate-x-1/2 bg-primary-magenta"></div>

          {/* Container for the reports */}
          <div className="relative py-8">
            {reports?.map((report) => (
              <OrgChartCompanyReport depth={2} key={report.id} employee={report} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgChartEmployeeTopLevel;
